import {
  ExerciseAssetDto,
  UserExerciseDto,
} from "@growth-machine-llc/stridist-api-client";
import { Box, BoxProps, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { ThumbnailVideoBadge } from "../exercise-library/ThumbnailVideoBadge";
import { WorkoutExercise } from "../workout/types";
import clsx from "clsx";
import MinimizedTooltip from "../tooltip/MinimizedTooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    minHeight: theme.spacing(9),
    gap: theme.spacing(1),
    padding: theme.spacing(2.5, 2),
    cursor: "pointer",

    "&:hover": {
      backgroundColor: theme.palette.selected.light,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  lozenge: {
    color: theme.palette.warning.main,
    height: 22,
  },
  badgeContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },

  category: {
    padding: theme.spacing(0.3, 1.2),
    alignItems: "center",
    height: 18,
    "& > *": {
      fontSize: 12,
    },
  },

  title: {
    fontSize: 14,
    fontWeight: 600,
  },

  badge: {
    height: 15,
    minWidth: 15,
    "& > *": {
      fontSize: 10,
    },
  },

  instruction: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "17px",
    color: theme.palette.text.secondary,
  },

  contentContainer: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    width: "100%",
    gap: theme.spacing(0.5),
  },
}));

export const shouldRenderExerciseBadges = (
  exerciseAsset: ExerciseAssetDto,
  showTags: boolean,
) => {
  const bodyparts = exerciseAsset?.bodyparts || [];
  const movementPatterns = exerciseAsset?.movementPatterns || [];
  const tags = exerciseAsset?.tags || [];

  return (
    bodyparts.length > 0 ||
    movementPatterns.length > 0 ||
    (showTags && tags.length > 0)
  );
};

export interface IClientExerciseListItem extends Omit<BoxProps, "onClick"> {
  userExercise: UserExerciseDto;
  isObsolete?: boolean;
  onClick?: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    value: UserExerciseDto,
  ) => void;
  displayTags?: boolean;
}

const ClientExerciseListItem = (props: IClientExerciseListItem) => {
  const {
    className,
    userExercise,
    onClick,
    isObsolete,
    displayTags = true,
    ...other
  } = props;
  const s = useStyles();
  const exercise = userExercise?.exercise;
  const optionContent =
    !!exercise && (JSON.parse(exercise.content) as WorkoutExercise);

  const handleClick = React.useCallback(
    (event) => {
      if (onClick) {
        onClick(event, userExercise);
      }
    },
    [userExercise, onClick],
  );

  return (
    <Box className={clsx(s.root, className)} onClick={handleClick} {...other}>
      <ThumbnailVideoBadge
        border={false}
        size={50}
        video={optionContent.video}
      />

      <Box className={s.contentContainer}>
        <Box className={s.header}>
          <Typography className={s.title}>
            {userExercise.title || exercise?.name}
          </Typography>
          {isObsolete && (
            <MinimizedTooltip
              children={<InfoOutlinedIcon className={s.lozenge} />}
              title={"This exercise was renamed or deleted by the coach."}
              opacity={1}
            />
          )}
        </Box>
        {optionContent?.instructions && (
          <Typography className={s.instruction}>
            {optionContent.instructions}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ClientExerciseListItem;
